'use strict';
const ee = require('event-emitter');
const Hawk = require('hawk');
const serialize = require('query-string').stringify;

const {CookiesClient} = require('@stubhub/react-cookies');

const {shouldApplyAuth} = require('./utils');
const cookie = new CookiesClient();

// eslint-disable-next-line no-magic-numbers
const DEFAULT_TIMEOUT = 30 * 1000; // 30 seconds
const BEARER_TOKEN_REGEX = /^Bearer\s/i;
const ENABLE_API_AUTH = /^1|true$/i.test(process.env.REACT_APP_ENABLE_API_AUTH);
const AUTH_COOKIE = process.env.REACT_APP_AUTH_COOKIE || 'SH_BAU';
const BYPASS_SEPARATOR = process.env.REACT_APP_BYPASS_SEPARATOR || ';';
const BYPASS_AUTH_LIST = process.env.REACT_APP_BYPASS_AUTH_LIST
  ? process.env.REACT_APP_BYPASS_AUTH_LIST.split(BYPASS_SEPARATOR)
  : [];

/**
 * Makes a request using the Fetch API.
 *
 * @param {Object} options - The options for the request.
 * @param {string} options.uri - The URI to request.
 * @param {string} [options.method='GET'] - The HTTP method to use.
 * @param {Object} [options.headers={}] - The headers to include in the request.
 * @param {number} [options.timeout=30000] - The timeout for the request, in milliseconds.
 * @param {Object} [options.qs] - Query string parameters to include in the request.
 * @param {Object} [options.json] - JSON data to include in the request.
 * @param {Object} [options.form] - Form data to include in the request.
 * @param {string} [options.cache] - The cache mode to use for the request.
 * @param {Function} callback - The callback to invoke when the request completes.
 * @returns {Fetch} A new Fetch instance.
 * @throws {Error} If the options are invalid.
 */
function request(options, callback) {
  // The entry-point to the API: prep the options object and pass the real work to run_xhr.
  if (typeof callback !== 'function') {
    throw new Error(`Bad callback given: ${callback}`);
  }

  if (!options) {
    throw new Error('No options given');
  }

  if (options.url) {
    options.uri = options.url;
    delete options.url;
  }

  if (!options.uri && options.uri !== '') {
    throw new Error('options.uri is a required argument');
  }

  if (typeof options.uri !== 'string') {
    throw new Error('options.uri must be a string');
  }

  const unsupported_options = ['auth', 'onResponse', 'proxy', '_redirectsFollowed', 'maxRedirects', 'followRedirect'];
  for (let i = 0; i < unsupported_options.length; i++) {
    if (options[unsupported_options[i]]) {
      throw new Error(`options.${unsupported_options[i]} is not supported`);
    }
  }

  options.callback = callback;
  options.method = options.method || 'GET';
  options.headers = options.headers || {};
  options.timeout = options.timeout || request.DEFAULT_TIMEOUT;

  if (options.headers.host) {
    throw new Error('options.headers.host is not supported');
  }

  if (options.json) {
    options.headers.accept = options.headers.accept || 'application/json';
    if (options.method !== 'GET') {
      options.headers['content-type'] = 'application/json';
    }

    if (typeof options.json !== 'boolean') {
      options.body = JSON.stringify(options.json);
    } else if (typeof options.body !== 'string') {
      options.body = JSON.stringify(options.body);
    }
  }

  if (options.qs) {
    const qs = typeof options.qs === 'string' ? options.qs : serialize(options.qs);
    if (options.uri.indexOf('?') !== -1) {
      // No get params
      options.uri = `${options.uri}&${qs}`;
    } else {
      // Existing get params
      options.uri = `${options.uri}?${qs}`;
    }
  }

  if (options.form) {
    if (typeof options.form === 'string') {
      throw 'form name unsupported';
    }
  }

  if (options.cache || typeof options.cache === 'boolean') {
    const cacheTypes = ['default', 'no-cache', 'reload', 'force-cache', 'only-if-cached'];
    options.cache = typeof options.cache === 'string' ? options.cache : options.cache ? 'default' : 'no-cache';
    if (!cacheTypes.includes(options.cache)) {
      throw 'cache must be one of the following: default, no-store, reload, no-cache, force-cache, only-if-cached';
    }
  }

  return new Fetch(options);
}

function Fetch(options) {
  const {method, headers, cache} = options;
  const opts = {
    method,
    headers,
    cache,
  };
  if (options.body) {
    opts.body = options.body;
  }
  if (options.credentials) {
    opts.credentials = options.credentials;
  }
  const uri = options.baseUrl ? options.baseUrl + options.uri : options.uri;

  const cred = cookie.getJSON(AUTH_COOKIE);
  const applyAuth = shouldApplyAuth(ENABLE_API_AUTH, BYPASS_AUTH_LIST, uri, cred);
  let auth;

  if (applyAuth) {
    const ext = opts.headers.Authorization && opts.headers.Authorization.replace(BEARER_TOKEN_REGEX, '');
    auth = Hawk.client.header(getFullURL(uri), options.method, {credentials: cred, ext});
    opts.headers.Authorization = auth.header;
  }

  const self = this;

  timeoutPromise(fetch(uri, opts), options.timeout)
    .then((res) => {
      res.statusCode = res.status;
      if (applyAuth) {
        const isValid = Hawk.client.authenticate(res, cred, auth.artifacts, {});
        const logger = options.logger || console; // Options.logger is non-standard but might be set by @stubhub/rest-method
        if (!isValid) {
          if (logger && typeof logger.warn === 'function') {
            logger.warn({message: 'Suspicious Server response', tag: 'client-hawk', url: uri});
          }

          /*
           * Throw new Error('Suspicious Server')
           * disable above line as Edge server could return the response with an incorrect hawk header (11/30/18)
           * updated Akamai (v474) to remove `www-authenticate` and `server-authorization` from cached API response (12/4/18)
           */
        }
      }
      self.emit('response', res);
      const value = options.json ? res.json() : res.text();

      // eslint-disable-next-line promise/always-return
      return value.then((v) => {
        options.callback(null, res, v);
      });
    })
    .catch((err) => {
      self.emit('error', err);
      options.callback(err);
    });

  return this;
}

ee(Fetch.prototype);

function timeoutPromise(promise, timeout) {
  return new Promise((resolve, reject) => {
    let timer = setTimeout(() => {
      const err = new Error('ETIMEDOUT');
      err.code = 'ETIMEDOUT';
      err.duration = timeout;
      reject(err);
    }, timeout);

    // eslint-disable-next-line promise/catch-or-return
    promise.then((v) => {
      resolve(v);
      // eslint-disable-next-line promise/always-return
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }, reject);
  });
}

request.DEFAULT_TIMEOUT = DEFAULT_TIMEOUT;

request.defaults = function (options) {
  const def = function (method) {
    const d = function (params, callback) {
      if (typeof params === 'string') {
        params = {uri: params};
      }
      for (const i in options) {
        if (params[i] === undefined) {
          params[i] = options[i];
        }
      }

      return method(params, callback);
    };

    return d;
  };
  const de = def(request);
  de.get = def(request.get);
  de.post = def(request.post);
  de.put = def(request.put);
  de.head = def(request.head);
  de.del = def(request.del);
  de.delete = def(request.delete);

  return de;
};

const shortcuts = ['get', 'put', 'post', 'head', 'del', 'delete'];
shortcuts.forEach((shortcut) => {
  const method = shortcut.toUpperCase();
  const func = shortcut.toLowerCase();

  request[func] = function (opts) {
    if (typeof opts === 'string') {
      opts = {method, uri: opts};
    } else {
      const {logger} = opts; // If exists, it's set by @stubhub/rest-method
      opts = JSON.parse(JSON.stringify(opts));
      opts.logger = logger; // Reset the logger to opts
      opts.method = method;
    }

    // eslint-disable-next-line prefer-rest-params
    const args = [opts].concat(Array.prototype.slice.apply(arguments, [1]));

    return request.apply(this, args);
  };
});

function getFullURL(uri) {
  if (/^\//.test(uri)) {
    // Starting with /
    return `${location.protocol}//${location.host}${uri}`;
  } else if (/^http/i.test(uri)) {
    // Starting with http
    return uri;
  }

  return location.toString() + uri;
}
module.exports = request;
